import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "@/components/base/PageHeader";
import EmptyState from "@/components/base/EmptyState";
import Pagination from "@/components/base/Pagination";
import { resourceFields, resourceList, resourceTypes } from "@/mocks/resources";

const PAGE_SIZE = 6;

export default function ResourcesList() {
  const [keyword, setKeyword] = useState("");
  const [type, setType] = useState("全部");
  const [field, setField] = useState("全部");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return resourceList.filter((item) => {
      const matchKeyword =
        !kw ||
        item.title.toLowerCase().includes(kw) ||
        item.summary.toLowerCase().includes(kw) ||
        item.tags.some((tag) => tag.toLowerCase().includes(kw));
      const matchType = type === "全部" || item.type === type;
      const matchField = field === "全部" || item.field === field;
      return matchKeyword && matchType && matchField;
    });
  }, [keyword, type, field]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setKeyword("");
    setType("全部");
    setField("全部");
    setPage(1);
  };

  return (
    <div className="w-full">
      <PageHeader
        breadcrumb={[{ label: "资源查询" }]}
        eyebrow="资源中心"
        title="资源查询"
        description="支持关键词检索与类型、领域组合筛选，快速定位你需要的白皮书、研究报告与实用工具。"
      />

      <section className="px-4 md:px-8 py-10 md:py-14">
        <div className="mx-auto max-w-6xl flex flex-col gap-8">
          <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
            <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2.5 focus-within:border-primary-400">
              <i className="ri-search-line text-base text-foreground-400" />
              <input
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setPage(1);
                }}
                type="text"
                placeholder="输入关键词检索资源，例如「白皮书」「品牌」"
                className="flex-1 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={() => setKeyword("")}
                  className="text-foreground-400 hover:text-foreground-700"
                  aria-label="清空关键词"
                >
                  <i className="ri-close-circle-line text-base" />
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="mr-1 text-sm font-medium text-foreground-700">资源类型</span>
              {resourceTypes.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => {
                    setType(item);
                    setPage(1);
                  }}
                  className={`rounded-full px-4 py-1.5 text-sm transition-colors whitespace-nowrap ${
                    type === item
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-600"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2 border-t border-background-200 pt-4">
              <span className="mr-1 text-sm font-medium text-foreground-700">相关领域</span>
              {resourceFields.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => {
                    setField(item);
                    setPage(1);
                  }}
                  className={`rounded-full px-4 py-1.5 text-sm transition-colors whitespace-nowrap ${
                    field === item
                      ? "bg-accent-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-accent-50 hover:text-accent-600"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground-500">
              共找到 <strong className="font-semibold text-foreground-900">{filtered.length}</strong> 项资源
            </span>
            {(keyword || type !== "全部" || field !== "全部") && (
              <button
                type="button"
                onClick={resetFilters}
                className="flex items-center gap-1 text-sm text-foreground-500 hover:text-accent-600"
              >
                <i className="ri-refresh-line" />
                重置
              </button>
            )}
          </div>

          {visible.length === 0 ? (
            <EmptyState
              title="没有匹配的资源"
              description="尝试更换关键词或调整筛选条件，也可以重置后浏览全部资源。"
              actionLabel="重置筛选"
              onAction={resetFilters}
            />
          ) : (
            <div data-product-shop className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((item) => (
                <Link
                  key={item.id}
                  to={`/resources/${item.id}`}
                  className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
                >
                  <div className="relative h-40 w-full overflow-hidden">
                    <img
                      src={item.cover}
                      alt={item.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <span className="absolute left-4 top-4 rounded-full bg-background-50/95 px-3 py-1 text-xs font-medium text-primary-700">
                      {item.type}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col gap-3 p-5">
                    <h2 className="font-heading text-base font-semibold leading-snug text-foreground-950 group-hover:text-primary-600">
                      {item.title}
                    </h2>
                    <p className="text-sm leading-relaxed text-foreground-600 line-clamp-2">
                      {item.summary}
                    </p>
                    <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
                      {item.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-md bg-background-100 px-2 py-0.5 text-xs text-foreground-500"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between border-t border-background-200 pt-3 text-xs text-foreground-500">
                      <span>{item.field}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-file-line" />
                        {item.format}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {visible.length > 0 && (
            <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          )}
        </div>
      </section>
    </div>
  );
}